import React from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Image,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Grid,
  GridItem,
  Divider,
  Icon,
  Tooltip,
  Switch,
  Progress,
  Flex,
  Tag,
  TagLabel,
  TagLeftIcon,
} from '@chakra-ui/react';
import { FiTrendingUp, FiClock, FiLock, FiUnlock, FiDollarSign } from 'react-icons/fi';
import { GiTreasureMap, GiCoins, GiChest, GiPirateFlag, GiPadlock, GiPadlockOpen } from 'react-icons/gi';

// Theme colors
const colors = {
  gold: "#FFD700",
  emerald: "#50C878",
  sapphire: "#0F52BA",
  ruby: "#E0115F",
  amethyst: "#9966CC",
  topaz: "#FFC87C",
};

// JINU price in USD
const JINU_PRICE = 0.136;

const GoldenKeyNFTCard = ({ nft }) => {
  const stakingProgress = (nft.stakingDays / nft.stakingTotal) * 100;
  const isFullyStaked = stakingProgress === 100;

  // Calculate USD values
  const totalEarnedUSD = (nft.totalEarned * JINU_PRICE).toFixed(2);
  const lastRewardUSD = (nft.lastReward * JINU_PRICE).toFixed(2);

  return (
    <Box
      borderWidth="1px"
      borderRadius="xl"
      overflow="hidden"
      bg="gray.900"
      borderColor={colors.gold}
      position="relative"
      _hover={{
        transform: 'translateY(-2px)',
        boxShadow: `0 0 20px ${colors.gold}40`,
        transition: 'all 0.2s',
      }}
    >
      <Grid templateColumns="350px 1fr" gap={0} height="100%">
        {/* Left side - Image */}
        <GridItem height="100%">
          <Box
            position="relative"
            bg="gray.800"
            p={4}
            borderRadius="xl"
            height="100%"
            boxShadow="0 0 20px rgba(255, 215, 0, 0.1)"
          >
            <Box
              position="relative"
              borderRadius="lg"
              overflow="hidden"
              border="1px solid"
              borderColor={colors.gold + "30"}
              boxShadow={`inset 0 0 20px ${colors.gold}30`}
              height="100%"
            >
              <Image
                src="/images/angels-key.gif"
                alt={`Angels Key #${nft.id}`}
                objectFit="cover"
                width="100%"
                height="100%"
                opacity={nft.isStaked ? 1 : 0.6}
                transition="opacity 0.2s"
              />
              
              {/* NFT Value Overlay */}
              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                bg="rgba(0,0,0,0.6)"
                backdropFilter="blur(5px)"
                p={3}
                borderTop="1px solid"
                borderColor={colors.gold + "30"}
              >
                <VStack spacing={1} align="start">
                  <Text fontSize="sm" color={colors.gold + "B3"}>
                    Estimated Value
                  </Text>
                  <HStack justify="space-between" width="100%">
                    <Text fontSize="lg" fontWeight="bold" color="#32CD32">
                      {(nft.totalEarned * 12).toLocaleString()} JINU
                    </Text>
                    <Text fontSize="sm" color="#32CD32">
                      ${(nft.totalEarned * 12 * JINU_PRICE).toFixed(2)}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="#32CD32">
                    {(nft.totalEarned / 30).toFixed(0)} JINU daily earnings × 365 days
                  </Text>
                </VStack>
              </Box>

              <Badge
                position="absolute"
                top={4}
                right={4}
                px={3}
                py={1}
                borderRadius="full"
                bg={colors.sapphire}
                color="white"
                fontSize="sm"
                textTransform="uppercase"
                boxShadow="0 0 10px rgba(15, 82, 186, 0.5)"
              >
                {nft.rarity}
              </Badge>
              
              {/* Staking Status Badge */}
              <Badge
                position="absolute"
                bottom={20}
                right={4}
                px={3}
                py={1}
                borderRadius="full"
                bg={nft.isStaked ? colors.emerald : 'gray.600'}
                color="white"
                fontSize="sm"
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Icon as={nft.isStaked ? GiPadlockOpen : GiPadlock} />
                {nft.isStaked ? 'Staked' : 'Unstaked'}
              </Badge>
            </Box>
          </Box>
        </GridItem>

        {/* Right side - Details */}
        <GridItem p={6}>
          <VStack align="stretch" spacing={4}>
            {/* Header */}
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color={colors.gold}>
                  Angels Key #{nft.id}
                </Text>
                <HStack spacing={2}>
                  <Tag size="sm" variant="subtle" colorScheme={isFullyStaked ? "green" : "yellow"}>
                    <TagLeftIcon as={isFullyStaked ? FiLock : FiUnlock} />
                    <TagLabel>{isFullyStaked ? "Fully Vested" : "Vesting"}</TagLabel>
                  </Tag>
                </HStack>
              </VStack>
              <Tooltip 
                label={nft.isStaked ? "Unstake NFT" : "Stake NFT for 10% Revenue Share"} 
                placement="top"
              >
                <Button
                  size="lg"
                  variant="outline"
                  borderWidth="2px"
                  borderColor="#E0115F"
                  color="#E0115F"
                  bg="transparent"
                  _hover={{
                    bg: "#E0115F20",
                    transform: "translateY(-1px)",
                    boxShadow: `0 0 10px #E0115F40`
                  }}
                >
                  {nft.isStaked ? 'Unstake' : 'Stake'}
                </Button>
              </Tooltip>
            </HStack>

            <Divider borderColor={colors.gold + "40"} />

            {/* Stats */}
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <Stat>
                <StatLabel color={colors.emerald}>Total Earned</StatLabel>
                <VStack spacing={0} align="start">
                  <StatNumber color="white" fontSize="2xl">
                    {nft.totalEarned.toLocaleString()} JINU
                  </StatNumber>
                  <Text fontSize="sm" color="gray.400">
                    <Icon as={FiDollarSign} /> {totalEarnedUSD} USD
                  </Text>
                </VStack>
                <StatHelpText color={colors.emerald}>
                  <StatArrow type="increase" />
                  10% Revenue Share
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel color={colors.ruby}>Last Reward</StatLabel>
                <VStack spacing={0} align="start">
                  <StatNumber color="white" fontSize="2xl">
                    {nft.lastReward.toLocaleString()} JINU
                  </StatNumber>
                  <Text fontSize="sm" color="gray.400">
                    <Icon as={FiDollarSign} /> {lastRewardUSD} USD
                  </Text>
                </VStack>
                <StatHelpText color={colors.ruby}>
                  <Icon as={FiClock} mr={1} />
                  24h ago
                </StatHelpText>
              </Stat>
            </Grid>

            {/* Benefits */}
            <Box>
              <Text mb={2} color={colors.amethyst} fontWeight="semibold">
                Key Benefits
              </Text>
              <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                <Tooltip label="Vote on DAO proposals and treasury adjustments">
                  <HStack color={colors.topaz}>
                    <Icon as={GiTreasureMap} />
                    <Text>Governance Rights</Text>
                  </HStack>
                </Tooltip>
                <Tooltip label="Earn 10% of total revenue when staked">
                  <HStack color={colors.topaz}>
                    <Icon as={GiCoins} />
                    <Text>Revenue Share</Text>
                  </HStack>
                </Tooltip>
                <Tooltip label="Early access to new features and events">
                  <HStack color={colors.topaz}>
                    <Icon as={GiChest} />
                    <Text>Priority Access</Text>
                  </HStack>
                </Tooltip>
                <Tooltip label="Access to exclusive community events">
                  <HStack color={colors.topaz}>
                    <Icon as={GiPirateFlag} />
                    <Text>Exclusive Events</Text>
                  </HStack>
                </Tooltip>
              </Grid>
            </Box>

            {/* Staking Progress */}
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text color={colors.sapphire} fontWeight="semibold">
                  Staking Progress
                </Text>
                <Text color={colors.sapphire}>
                  {nft.stakingDays} days / {nft.stakingTotal} days
                </Text>
              </HStack>
              <Tooltip label={`${Math.round(stakingProgress)}% Complete`}>
                <Progress 
                  value={stakingProgress}
                  size="sm" 
                  borderRadius="full"
                  colorScheme={isFullyStaked ? "green" : "blue"}
                  bg="gray.700"
                  hasStripe={!isFullyStaked}
                  isAnimated={!isFullyStaked}
                />
              </Tooltip>
            </Box>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default GoldenKeyNFTCard;
